/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The registered insights as a catalogue: what the admin permission editors list and
 * what the dashboard uses to decide whether a configured insight is still available.
 * Pure module — no React.
 */

import { isInsightAllowed } from '@/insights/insightAccess'
import { categories, insights } from '@/insights/insights'
import { camelToTitleCase } from '@/utils/camelToTitleCase'

export interface CatalogueInsight {
  slug: string
  name: string
  description: string
  category: string
}

export interface CatalogueCategory {
  key: string
  name: string
  insights: CatalogueInsight[]
}

const categoryNames: Record<string, { name: string } | undefined> = categories

/** Display name for a definition's `category` key; falls back to Title Case of the key. */
export const categoryName = (key: string): string => categoryNames[key]?.name ?? camelToTitleCase(key)

/** Every registered slug, in registration order. */
export const REGISTERED_SLUGS: readonly string[] = Object.keys(insights)

export const TOTAL_INSIGHTS = REGISTERED_SLUGS.length

const definitionOf = (slug: string) => (insights as Record<string, (typeof insights)[keyof typeof insights]>)[slug]

export const isRegisteredInsight = (slug: string): boolean => definitionOf(slug) !== undefined

export const insightName = (slug: string): string => definitionOf(slug)?.name ?? slug

const buildCatalogue = (): CatalogueCategory[] => {
  const byCategory = new Map<string, CatalogueInsight[]>()
  for (const slug of REGISTERED_SLUGS) {
    const definition = definitionOf(slug)
    if (!definition) continue
    const entry: CatalogueInsight = {
      slug,
      name: definition.name,
      description: definition.description,
      category: definition.category,
    }
    const list = byCategory.get(definition.category) ?? []
    list.push(entry)
    byCategory.set(definition.category, list)
  }
  return [...byCategory.entries()]
    .map(([key, list]) => ({
      key,
      name: categoryName(key),
      insights: [...list].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** Categories sorted by display name, each with its insights sorted by name. */
export const CATALOGUE: CatalogueCategory[] = buildCatalogue()

export { isInsightAllowed }
